import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, Signal, TemplateRef, viewChild } from '@angular/core';
import { PreloaderColor } from '@enums/shared.enum';
import { features } from '@consts/text.const';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UiToggleComponent } from '@ui';
import { CameraModel, SharedModels, ZoneRegionsModels } from '@share-models';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../store/app.state';
import { TranslatePipe } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UiButtonComponent, UiFilterChipComponent, UiNoDataComponent } from '@ui';
import { combineLatest, debounceTime, distinctUntilChanged, map, shareReplay, take, withLatestFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmDialogModel, ConfirmDialogService } from '@ui';
import { TranslateService } from '@ngx-translate/core';
import { ZoneRegionsActions } from '@states/zone-regions/zone-regions.actions';
import { zoneRegionsFeature } from '@states/zone-regions/zone-regions.feature';
import { ZoneRegionEditorActions } from '@states/zone-region-editor/zone-region-editor.actions';
import { Dictionary } from '@ngrx/entity/src';
import { EdgeCamera } from '../../../../cameras/camera.model';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { MatDialog } from '@angular/material/dialog';
import { CameraPickerModels } from '@modals/camera-picker-inline/models/models';
import { CameraPickerModalComponent } from '@modals/camera-picker-modal/camera-picker-modal.component';
import { cloneDeep } from 'lodash';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { NgForOf, NgIf } from '@angular/common';
import { UiCameraSelectButtonComponent } from '@ui';
import { UiPreloaderComponent } from '../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiSearchComponent } from '../../../../shared/ui-kit/ui-search/ui-search.component';
import { MatTooltip } from '@angular/material/tooltip';
import { CellTemplateContext, DatasetTableConfig, UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createZoneRegionsTableConfig } from './zone-regions-table.util';
import { generateCameraIdsIncludesExcludes, JsonParseIfValid } from '../../../../helpers/common.helpers';
import { MenuActions } from '@states/menu/menu.action-types';
import { MenuKey } from '@enums/menu.enum';
import { ZoneRegionDialogService } from '../../../../services/zone-region-dialog.service';

@Component({
  selector: 'app-zone-regions',
  templateUrl: './zone-regions.component.html',
  styleUrl: './zone-regions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatMenu,
    NgForOf,
    TranslatePipe,
    UiCameraSelectButtonComponent,
    UiFilterChipComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiSearchComponent,
    UiButtonComponent,
    MatTooltip,
    MatMenuTrigger,
    ReactiveFormsModule,
    FormsModule,
    UiToggleComponent,
    UiDatasetTableComponent,
  ],
})
export class ZoneRegionsComponent implements OnInit {
  protected readonly Object = Object;
  private store$ = inject(Store<AppState>);
  private dialog = inject(MatDialog);
  private zoneRegionDialogService = inject(ZoneRegionDialogService);
  private confirmDialogService = inject(ConfirmDialogService);
  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  private initialFilters: ZoneRegionsModels.Forms.FiltersFormValue = {
    query: '',
    cameraIds: {},
    tagIds: [],
  };

  public filtersForm = new FormGroup<ZoneRegionsModels.Forms.FiltersForm>({
    query: new FormControl(this.initialFilters.query),
    cameraIds: new FormControl<Record<string, CameraModel.SelectedCamera>>(this.initialFilters.cameraIds),
    tagIds: new FormControl<string[]>(this.initialFilters.tagIds),
  });

  public loaderColor = PreloaderColor;
  public initialEmpty = features.zoneRegions.initialEmpty;
  public noResults = features.zoneRegions.noResults;
  public viewStates = SharedModels.ViewState;

  public selectIsLoading: Signal<boolean> = this.store$.selectSignal(zoneRegionsFeature.selectIsLoading);
  public selectZoneRegions: Signal<ZoneRegionsModels.ZoneRegionDocument[]> = this.store$.selectSignal(zoneRegionsFeature.selectZoneRegions);
  public selectProcessingIds: Signal<string[]> = this.store$.selectSignal(zoneRegionsFeature.selectProcessingIds);
  public selectCameras: Signal<Dictionary<EdgeCamera.CameraItem>> = this.store$.selectSignal(CameraSelectors.selectCamerasLookup);

  protected readonly countingCellTemplate = viewChild<TemplateRef<CellTemplateContext<ZoneRegionsModels.ZoneRegionDocument>>>('countingCell');

  private readonly processingIdsSet: Signal<Set<string>> = computed(() => new Set(this.selectProcessingIds()));

  public tableConfig = computed<DatasetTableConfig<ZoneRegionsModels.ZoneRegionDocument> | null>(() => {
    const countingTemplate = this.countingCellTemplate();
    if (!countingTemplate) {
      return null;
    }
    return createZoneRegionsTableConfig({
      processingIdsSet: () => this.processingIdsSet(),
      countingCellTemplate: countingTemplate,
    });
  });

  public isProcessing(id: string): boolean {
    return this.processingIdsSet().has(id);
  }

  public onToggleZoneCounting(row: ZoneRegionsModels.ZoneRegionDocument, countingEnabled: boolean): void {
    this.store$.dispatch(ZoneRegionsActions.startSetZoneCounting({ id: row._id, countingEnabled }));
  }

  public get hasActiveFilters() {
    const filters = this.filtersForm.getRawValue();

    return Boolean(filters?.query)
      || Boolean(Object.keys(filters?.cameraIds).length)
      || (filters?.tagIds ?? []).length > 0;
  }

  public readonly viewState = computed(() => {
    if (this.selectZoneRegions().length > 0) {
      return SharedModels.ViewState.HAS_CONTENT;
    }

    return this.hasActiveFilters
      ? SharedModels.ViewState.NO_RESULTS
      : SharedModels.ViewState.INITIAL_EMPTY;
  });

  public ngOnInit(): void {
    this.store$.dispatch(MenuActions.setActiveLevel2Menu({ activeLevel2: MenuKey.zoneRegions }));
    this.store$.dispatch(ZoneRegionEditorActions.startLoadingAllZoneRegions());
    this.syncFilters();
  }

  public cleanAllFilters() {
    this.filtersForm.reset(this.initialFilters);
  }

  /** Clear only the search query; keeps camera/tag filters. */
  public onRemoveQueryFilter(): void {
    this.filtersForm.get('query').setValue('');
  }

  public cleanTagsFilterControl(tag: string) {
    const current = this.filtersForm.get('tagIds').value ?? [];
    const updated = current.filter((v) => v !== tag);
    this.filtersForm.get('tagIds').patchValue(updated);
  }

  public selectCameraFilter(): void {
    const cams = cloneDeep(this.filtersForm.get('cameraIds').value);

    const data: CameraPickerModels.CameraPickerDialogOptions = {
      options: { multi: true },
      selectedValue: cams,
    };
    this.dialog
      .open(CameraPickerModalComponent, {
        width: '600px',
        height: '70vh',
        panelClass: 'modal-no-padding',
        disableClose: true,
        data,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedCameras: Dictionary<CameraModel.SelectedCamera>) => {
        if (selectedCameras) {
          this.filtersForm.get('cameraIds').setValue(selectedCameras);
        }
      });
  }

  public onRemoveCameraFilter(cameraId: string): void {
    const current = { ...this.filtersForm.get('cameraIds').value };

    if (current[cameraId]) {
      delete current[cameraId];
      this.filtersForm.get('cameraIds').setValue(current);
    }
  }

  public onLoadMore(): void {
    const filters = this.toApiFilters(this.parseQueryParamFilters());
    this.store$.dispatch(ZoneRegionsActions.getZoneRegions({ filters }));
  }

  public create(): void {
    this.zoneRegionDialogService.openCreateWithNameDialog();
  }

  public onRowAction(event: { actionId: string; row: ZoneRegionsModels.ZoneRegionDocument }): void {
    if (event.actionId === 'view') {
      this.router.navigate([event.row._id], { relativeTo: this.route });
      return;
    }
    if (event.actionId === 'edit') {
      this.zoneRegionDialogService.openEditZoneRegionDialog(event.row);
      return;
    }
    if (event.actionId === 'delete') {
      const name = ZoneRegionsModels.getZoneRegionName(event.row) ?? this.translateService.instant('ZONE_REGIONS.UNTITLED_ZONE_REGION');
      this.confirmDialogService
        .open({
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          title: this.translateService.instant('ZONE_REGIONS.DIALOGS.DELETE.TITLE'),
          msg: this.translateService.instant('ZONE_REGIONS.DIALOGS.DELETE.MESSAGE', { name: `"${name}"` }),
          confirm: this.translateService.instant('COMMON.DELETE'),
          disableClose: true,
        })
        .pipe(take(1))
        .subscribe((result) => {
          if (result?.selection === ConfirmDialogModel.ConfirmResult.OK) {
            this.store$.dispatch(ZoneRegionsActions.startDeleteZoneRegion({ id: event.row._id }));
          }
        });
    }
  }

  private parseQueryParamFilters(): { query: string; cameraIdsIncludes: string[]; cameraIdsExcludes: string[]; tagIds: string[] } {
    const qp = this.route.snapshot.queryParamMap;
    const query = (qp.get('query') ?? '').trim();
    const cameraIdsIncludes = JsonParseIfValid<string[] | null>(qp.get('cameraIdsIncludes') ?? '') ?? [];
    const cameraIdsExcludes = JsonParseIfValid<string[] | null>(qp.get('cameraIdsExcludes') ?? '') ?? [];
    const tagIds = (qp.get('tagIds') ?? '').split(',').map((s) => s.trim()).filter(Boolean);

    return { query, cameraIdsIncludes, cameraIdsExcludes, tagIds };
  }

  private toApiFilters(filters: { query: string; cameraIdsIncludes: string[]; cameraIdsExcludes: string[]; tagIds: string[] }): ZoneRegionsModels.Filters {
    return {
      query: filters.query,
      cameraIdsIncludes: filters.cameraIdsIncludes,
      cameraIdsExcludes: filters.cameraIdsExcludes,
      tagIds: filters.tagIds.join(','),
    };
  }

  private cameraItemToSelectedCamera(cam?: EdgeCamera.CameraItem | null): CameraModel.SelectedCamera | null {
    if (!cam || !cam.edgeOnly?.cameraId || !cam.edgeId) return null;

    return {
      cameraId: cam.edgeOnly.cameraId,
      locationId: cam.locationId,
      edgeId: cam.edgeId,
    };
  }

  private resolveCameraIds(
    allowedCameraIds: string[],
    cameraIdsIncludes?: string[] | null,
    cameraIdsExcludes?: string[] | null,
  ): string[] {
    if (cameraIdsIncludes?.length) return cameraIdsIncludes;
    if (cameraIdsExcludes?.length) return allowedCameraIds.filter((id) => !cameraIdsExcludes.includes(id));
    return [];
  }

  public syncFilters(): void {
    const sameUrlFilters = (a, b) =>
      a.query === b.query &&
      a.cameraIdsIncludes?.join(',') === b.cameraIdsIncludes?.join(',') &&
      a.cameraIdsExcludes?.join(',') === b.cameraIdsExcludes?.join(',') &&
      a.tagIds?.join(',') === b.tagIds?.join(',');

    const qpCriteria$ = this.route.queryParamMap.pipe(
      map((qp) => {
        const query = (qp.get('query') ?? '').trim();
        const cameraIdsIncludes = JsonParseIfValid<string[] | null>(qp.get('cameraIdsIncludes') ?? '') ?? [];
        const cameraIdsExcludes = JsonParseIfValid<string[] | null>(qp.get('cameraIdsExcludes') ?? '') ?? [];
        const tagIds = (qp.get('tagIds') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
        return { query, cameraIdsIncludes, cameraIdsExcludes, tagIds };
      }),
      distinctUntilChanged(sameUrlFilters),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const cameraLookup$ = this.store$.select(CameraSelectors.selectCamerasLookup).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    // Sync URL -> Filter Form
    combineLatest([qpCriteria$, cameraLookup$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([filters, cameraLookup]) => {
        const selectedDict: Record<string, CameraModel.SelectedCamera> = {};
        if (cameraLookup) {
          const allowedCameraIds = Object.keys(cameraLookup);
          const resolvedCameraIds = this.resolveCameraIds(
            allowedCameraIds,
            filters.cameraIdsIncludes,
            filters.cameraIdsExcludes,
          );
          for (const id of resolvedCameraIds) {
            const selected = this.cameraItemToSelectedCamera(cameraLookup[id]);
            if (selected) selectedDict[id] = selected;
          }
        }

        this.filtersForm.setValue(
          { query: filters.query, cameraIds: selectedDict, tagIds: filters.tagIds },
          { emitEvent: false },
        );
      });

    // Sync Form -> URL
    this.filtersForm.valueChanges
      .pipe(
        debounceTime(300),
        withLatestFrom(cameraLookup$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([value, cameraLookup]) => {
        const allowedCameraIds = Object.keys(cameraLookup ?? {});
        const pickedCamerasMap = value.cameraIds || {};
        let cameraIdsIncludes: string[] = [];
        let cameraIdsExcludes: string[] = [];

        if (allowedCameraIds.length) {
          ({ cameraIdsIncludes, cameraIdsExcludes } = generateCameraIdsIncludesExcludes(pickedCamerasMap, allowedCameraIds));
        } else {
          cameraIdsIncludes = Object.keys(pickedCamerasMap);
        }

        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            query: value.query || null,
            cameraIdsIncludes: cameraIdsIncludes.length ? JSON.stringify(cameraIdsIncludes) : null,
            cameraIdsExcludes: cameraIdsExcludes.length ? JSON.stringify(cameraIdsExcludes) : null,
            cameraIds: null,
            tagIds: (value.tagIds || []).join(',') || null,
          },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      });

    // URL -> dispatch load
    combineLatest([qpCriteria$, cameraLookup$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([filters]) => {
        this.store$.dispatch(ZoneRegionsActions.startLoadingZoneRegions({ filters: this.toApiFilters(filters) }));
      });
  }
}
